<template>
  <div class="nav-assistant">
    <!-- 遮罩层 -->
    <transition name="fade">
      <div
        v-if="isOpen"
        class="nav-assistant__overlay"
        @click="closePanel"
      ></div>
    </transition>

    <!-- 右侧抽屉面板 -->
    <transition name="slide-right">
      <div v-show="isOpen" class="nav-assistant__sheet">
        <div class="nav-assistant__content">
          <iframe
            ref="assistantIframe"
            :src="assistantIframeUrl"
            title="智能导航助手"
            class="nav-assistant__iframe"
            allow="clipboard-read; clipboard-write"
          ></iframe>
          <button
            class="nav-assistant__close"
            type="button"
            aria-label="关闭"
            @click="handleCollapse"
          >
            <img src="@/assets/navAssistant/right-top-close.png" alt="关闭" />
          </button>
        </div>
      </div>
    </transition>

    <!-- 浮动触发按钮 -->
    <div
      v-show="!isOpen"
      class="nav-assistant__fab-wrapper"
      :style="fabWrapperStyle"
    >
      <div
        ref="fab"
        class="nav-assistant__fab"
        :class="{ 'nav-assistant__fab--snapping': isSnapping }"
        @touchstart="onFabPointerDown"
        @touchmove.prevent="onFabPointerMove"
        @touchend="onFabPointerUp"
        @mousedown="onFabPointerDown"
      >
        <img
          v-if="currentGif === 'dance'"
          src="@/assets/navAssistant/dance.gif"
          alt="展开"
        />
        <img v-else src="@/assets/navAssistant/hello.gif" alt="展开" />
      </div>
    </div>
  </div>
</template>

<script>
// 宿主系统标识，用于 host_command 消息的 source 字段
const SOURCE = "campus-mobile";

// 简易环境判断
function getEnv() {
  const host = window.location.host;
  if (host.includes("sit")) return "sit";
  if (host.includes("dev")) return "dev";
  if (host.includes("localhost") || host.includes("127.0.0.1")) return "dev";
  return "prod";
}

export default {
  name: "navAssistant",

  data() {
    return {
      isOpen: false,
      isLoggedIn: false,
      iframeReady: false,
      // 拖动相关
      fabPos: { x: 0, y: 0 }, // 偏移量，0 表示使用默认位置
      isSnapping: false,
      dragState: {
        active: false,
        moved: false,
        startX: 0,
        startY: 0,
        startFabX: 0,
        startFabY: 0,
        fabW: 160,
        fabH: 160
      },
      // GIF 轮播
      currentGif: "hello",
      gifTimer: null
    };
  },

  computed: {
    assistantIframeUrl() {
      const route = "nav-assistant-mobile";
      const channel = "campus-mobile";
      if (process.env.NODE_ENV === "development") {
        return `http://localhost:5173/?channel=${channel}#/${route}`;
      }
      return `/static-resources/recruit-agent-oss/index.html?channel=${channel}#/${route}`;
    },
    personInfo() {
      return this.$store.state.global.personInfo || {};
    },
    fabWrapperStyle() {
      if (this.fabPos.x || this.fabPos.y) {
        return {
          left: this.fabPos.x + "px",
          top: this.fabPos.y + "px",
          right: "auto",
          bottom: "auto"
        };
      }
      return {};
    }
  },

  watch: {
    // 监听登录态变化
    "$store.state.global.personInfo": {
      handler(newVal) {
        const info = newVal || {};

        this.isLoggedIn = !!(info.userName || info.userKey);
        // 面板已展开时，同步登录态给 iframe
        if (this.isOpen) {
          this.sendLoginStateToIframe();
        }
      },
      deep: true
    }
  },

  mounted() {
    // 初始化登录态
    const info = this.personInfo;
    this.isLoggedIn = !!(info.userName || info.userKey);

    window.addEventListener("message", this.handleIframeMessage);
    window.addEventListener(
      "nav-assistant:open-and-send",
      this.handleOpenAndSend
    );
    window.addEventListener(
      "nav-assistant:send-login-state",
      this.sendLoginStateToIframe
    );

    // 启动 GIF 轮播（默认 dance，每 5 分钟切换）
    this.startGifTimer();
  },

  beforeDestroy() {
    window.removeEventListener("message", this.handleIframeMessage);
    window.removeEventListener(
      "nav-assistant:open-and-send",
      this.handleOpenAndSend
    );
    window.removeEventListener(
      "nav-assistant:send-login-state",
      this.sendLoginStateToIframe
    );
    this.stopGifTimer();
  },

  methods: {
    // ========== GIF 轮播 ==========

    startGifTimer() {
      this.gifTimer = setInterval(() => {
        this.currentGif = this.currentGif === "dance" ? "hello" : "dance";
      }, 5 * 60 * 1000);
    },

    stopGifTimer() {
      if (this.gifTimer) {
        clearInterval(this.gifTimer);
        this.gifTimer = null;
      }
    },

    // ========== 面板控制 ==========

    openPanel(skipLoginSync = false) {
      this.postMessageToIframe({
        source: SOURCE,
        action: "host_command",
        data: {
          type: "toggle_panel",
          payload: { state: "expanded" }
        }
      });
    },

    closePanel() {
      this.postMessageToIframe({
        source: SOURCE,
        action: "host_command",
        data: {
          type: "toggle_panel",
          payload: { state: "collapsed" }
        }
      });
    },

    handleCollapse() {
      this.closePanel();
    },

    sendLoginStateToIframe(event) {
      // 有 event 参数说明是从事件触发的，如果 iframe 还没就绪则不处理
      // 因为 onIframeReady() 会在 iframe 就绪时主动同步登录态
      if (event && !this.iframeReady) {
        return;
      }

      // 优先用传进来的值
      let isLoggedIn = this.isLoggedIn;
      let userKey = "";

      if (event && event.detail !== void 0) {
        // 支持对象格式 { userKey: '...' }
        if (typeof event.detail === "object" && event.detail !== null) {
          userKey = event.detail.userKey || "";
          isLoggedIn = !!userKey;
        } else {
          // 兼容旧的布尔值格式
          isLoggedIn = !!event.detail;
        }
      } else {
        // 没有 event 参数时，从 store 中获取 userKey
        const personInfo = this.$store.state.global.personInfo;
        if (personInfo && personInfo.userKey) {
          userKey = personInfo.userKey;
          isLoggedIn = true;
        }
      }

      this.postMessageToIframe({
        source: SOURCE,
        action: "host_command",
        data: {
          type: "toggle_login_guide",
          payload: { isLoggedIn, userKey }
        }
      });
    },

    // ========== postMessage 通信 ==========

    postMessageToIframe(message) {
      const iframe = this.$refs.assistantIframe;
      if (iframe && iframe.contentWindow) {
        const targetOrigin = this.getIframeOrigin();
        iframe.contentWindow.postMessage(message, targetOrigin);
      }
    },

    getIframeOrigin() {
      if (window._global && window._global.ENV === "dev") {
        return "http://localhost:5173";
      }
      return window.location.origin;
    },

    handleIframeMessage(event) {
      const expectedOrigin = this.getIframeOrigin();
      if (event.origin !== expectedOrigin) {
        if (process.env.NODE_ENV !== "development") {
          return;
        }
      }

      const msg = event.data || {};

      // 处理 nav-assistant 标准消息格式
      if (msg.source === "nav-assistant") {
        switch (msg.action) {
          case "request_assistant_state":
            this.handleAssistantStateRequest(msg);
            return;
          case "host_action":
            this.handleHostActionFromIframe(msg);
            return;
          default:
            break;
        }
      }

      // 处理其他 type 格式的消息
      const { type, payload } = msg;
      switch (type) {
        case "iframe_ready":
          this.onIframeReady();
          break;
        case "navigate":
          this.handleNavigateAction(payload);
          break;
        case "check_login":
          this.handleCheckLogin(payload);
          break;
        case "trigger_assistant_message":
          this.handleTriggerAssistantMessage(payload);
          break;
        default:
          break;
      }
    },

    handleAssistantStateRequest(msg) {
      const state = msg.data && msg.data.state;
      if (state === "expanded") {
        if (!this.isOpen) {
          this.isOpen = true;
        }
      } else if (state === "collapsed") {
        if (this.isOpen) {
          this.isOpen = false;
        }
      }
    },

    handleHostActionFromIframe(msg) {
      const { type, payload } = msg.data || {};
      this.handleNavigateAction({ action: type, data: payload });
    },

    onIframeReady() {
      this.iframeReady = true;
      try {
        this.postMessageToIframe({
          type: "host_init",
          payload: {
            isLoggedIn: this.isLoggedIn,
            env: getEnv() || "prod"
          }
        });
        // iframe 就绪后，如果 store 中已有 userKey，同步登录状态
        const personInfo = this.$store.state.global.personInfo;
        if (personInfo && personInfo.userKey) {
          this.sendLoginStateToIframe();
        }
      } catch (error) {
        console.error(error);
      }
    },

    // ========== 宿主导航动作处理 ==========

    handleNavigateAction(payload) {
      const { action, data } = payload || {};

      const loginRequiredActions = ["apply_resume", "view_lecture_list"];
      if (loginRequiredActions.includes(action) && !this.isLoggedIn) {
        this.goLoginWithRedirect();
        this.sendActionResult(action, false, null, "请先登录");
        return;
      }

      switch (action) {
        // case "apply_resume":
        //   this.handleApplyResume(data);
        //   break;
        case "view_job_detail":
          this.handleViewJobDetail(data);
          break;
        // case "view_lecture_list":
        //   this.handleViewLectureList(data);
        //   break;
        case "go_login":
          this.goLoginWithRedirect(data);
          break;
        case "navigate":
          this.handleCustomNavigate(data);
          break;
        case "resume_upload_success":
          this.handleResumeUploadSuccess(data);
          break;
        default:
          console.warn("[navAssistant] 未知的导航动作:", action);
          this.sendActionResult(action, false, null, "未知动作");
          break;
      }
    },

    // handleApplyResume(data) {
    //   const { postId } = data || {};
    //   if (postId) {
    //     this.$router.push({
    //       path: "/apply",
    //       query: { id: postId, fromPage: "navAssistant" }
    //     });
    //     this.closePanel();
    //     this.sendActionResult("apply_resume", true, { postId });
    //   } else {
    //     this.sendActionResult("apply_resume", false, null, "缺少职位ID参数");
    //   }
    // },

    handleViewJobDetail(data) {
      const { postId } = data || {};
      if (postId) {
        this.$router.push({
          path: `/positionDetail/${postId}`,
          query: { fromPage: "navAssistant" }
        });
        this.closePanel();
        this.sendActionResult("view_job_detail", true, { postId });
      } else {
        this.sendActionResult("view_job_detail", false, null, "缺少职位ID参数");
      }
    },

    // handleViewLectureList() {
    //   this.$router.push({ path: "/scheduleList" });
    //   this.closePanel();
    //   this.sendActionResult("view_lecture_list", true);
    // },

    goLoginWithRedirect(data) {
      const { redirectPath } = data || {};
      const query = redirectPath ? { redirect: redirectPath } : {};
      this.$router.push({ path: "/login", query });
      this.closePanel();
    },

    handleCustomNavigate(data) {
      const { path, query } = data || {};
      if (path) {
        let formatQuery = {};
        if (query) {
          const params = new URLSearchParams(query);
          formatQuery = Object.fromEntries(params.entries());
        }
        // 特殊映射：个人中心-投递动态 → 投递记录
        if (path === "/personalCenter" && formatQuery.tab === "applyDynamics") {
          this.$router.push({ path: "/applyList" });
        } else {
          this.$router.push({ path, query: formatQuery });
        }
        this.closePanel();
        this.sendActionResult("navigate", true, { path });
      }
    },

    handleResumeUploadSuccess(data) {
      console.log("[navAssistant] 简历上传解析成功:", data);
      this.closePanel();
      this.$store.dispatch("applyAnalysisResume", data);
      this.$router.push("/resume");
    },

    handleCheckLogin() {
      const info = this.personInfo;
      this.isLoggedIn = !!(info.userName || info.userKey);
    },

    handleTriggerAssistantMessage(payload) {
      const { message } = payload || {};
      if (message) {
        console.log("[navAssistant] 触发助手消息:", message);
      }
    },

    // ========== 浮动按钮拖动 ==========

    getPointerPos(e) {
      if (e.touches && e.touches.length) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
      return { x: e.clientX, y: e.clientY };
    },

    onFabPointerDown(e) {
      // 仅允许左键或触摸
      if (e.type === "mousedown" && e.button !== 0) return;

      const pos = this.getPointerPos(e);
      const fab = e.currentTarget;
      const rect = fab.getBoundingClientRect();

      this.isSnapping = false;
      this.dragState = {
        active: true,
        moved: false,
        startX: pos.x,
        startY: pos.y,
        startFabX: rect.left,
        startFabY: rect.top,
        fabW: rect.width,
        fabH: rect.height
      };

      // 鼠标模式需要监听 window 事件
      if (e.type === "mousedown") {
        window.addEventListener("mousemove", this.onMouseMove);
        window.addEventListener("mouseup", this.onMouseUp);
      }
    },

    onFabPointerMove(e) {
      if (!this.dragState.active) return;

      const pos = this.getPointerPos(e);
      const dx = pos.x - this.dragState.startX;
      const dy = pos.y - this.dragState.startY;

      // 超过阈值才算拖动，避免误触
      if (!this.dragState.moved && Math.abs(dx) < 5 && Math.abs(dy) < 5) {
        return;
      }

      this.dragState.moved = true;

      const { fabW, fabH } = this.dragState;
      const newX = Math.max(
        0,
        Math.min(window.innerWidth - fabW, this.dragState.startFabX + dx)
      );
      const newY = Math.max(
        0,
        Math.min(window.innerHeight - fabH, this.dragState.startFabY + dy)
      );

      this.fabPos = { x: newX, y: newY };
    },

    onFabPointerUp(e) {
      if (!this.dragState.active) return;

      const wasMouse = e.type === "mouseup";

      // 没有移动则视为点击
      if (!this.dragState.moved) {
        this.openPanel();
        this.dragState.active = false;
        if (wasMouse) {
          window.removeEventListener("mousemove", this.onMouseMove);
          window.removeEventListener("mouseup", this.onMouseUp);
        }
        return;
      }

      // 拖动结束：吸附到右侧同水平线
      this.isSnapping = true;
      const { fabW, fabH } = this.dragState;
      const snapX = window.innerWidth - fabW;
      const clampedY = Math.max(
        0,
        Math.min(window.innerHeight - fabH, this.fabPos.y)
      );
      this.fabPos = { x: snapX, y: clampedY };

      this.dragState.active = false;

      // 吸附动画结束后清理
      setTimeout(() => {
        this.isSnapping = false;
      }, 300);

      if (wasMouse) {
        window.removeEventListener("mousemove", this.onMouseMove);
        window.removeEventListener("mouseup", this.onMouseUp);
      }
    },

    onMouseMove(e) {
      this.onFabPointerMove(e);
    },

    onMouseUp(e) {
      this.onFabPointerUp(e);
    },

    // ========== 全局事件 ==========

    openAndSendMessage(message) {
      console.log("[navAssistant] 发送 host_command:", message);
      this.openPanel(true);
      this.$nextTick(() => {
        this.postMessageToIframe({
          source: SOURCE,
          action: "host_command",
          data: {
            type: "send_prompt",
            payload: { content: message }
          }
        });
      });
    },

    handleOpenAndSend(event) {
      if (!event.detail) {
        throw new Error("缺少提示词");
      }
      const message = event.detail;
      this.openAndSendMessage(message);
    },

    sendActionResult(action, success, data, errorMsg) {
      this.postMessageToIframe({
        type: "action_result",
        payload: { action, success, data, errorMsg }
      });
    }
  }
};
</script>

<style scoped>
.nav-assistant {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9999;
  pointer-events: none;
}

/* 遮罩层 */
.nav-assistant__overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1;
  pointer-events: auto;
}

/* 右侧抽屉面板 */
.nav-assistant__sheet {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  z-index: 2;
  pointer-events: auto;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.12);
}

.nav-assistant__content {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.nav-assistant__iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

/* 关闭按钮：使用 right-top-close.png 整图(白色圆形 + 紫色叉)，右上角悬浮 */
.nav-assistant__close {
  position: absolute;
  top: 32px;
  right: 32px;
  z-index: 3;
  display: block;
  width: 48px;
  height: 48px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.nav-assistant__close img {
  display: block;
  width: 100%;
  height: 100%;
}

/* 浮动触发按钮 */
.nav-assistant__fab-wrapper {
  position: fixed;
  right: 16px;
  bottom: 140px;
  display: flex;
  align-items: center;
  pointer-events: auto;
  z-index: 10;
}

.nav-assistant__fab {
  width: 160px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  touch-action: none;
}

.nav-assistant__fab img {
  width: 100%;
  pointer-events: none;
}

/* 吸附动画过渡 */
.nav-assistant__fab--snapping {
  transition: left 0.3s ease, top 0.3s ease;
}

.nav-assistant__fab-icon {
  width: 26px;
  height: 26px;
  color: #fff;
}

/* 过渡动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease;
}
.slide-right-enter,
.slide-right-leave-to {
  transform: translateX(100%);
}
</style>



// WEBPACK FOOTER //
// src/components/navAssistant/index.vue